import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import SavedDrafts from './SavedDrafts';
import DraftBoard from './DraftBoard';
import Auth from '../../../components/Auth';
import { useDraft } from '../contexts/DraftContext';

/* ─── Hamburger button ─────────────────────────────────────────────────────── */

const HamburgerBtn = styled.button`
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 1200;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #1e1e2e;
  border: 1px solid #444;
  color: #f0d060;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #2a2a3e;
    border-color: #666;
  }
`;

const Bar = styled.span`
  display: block;
  width: 20px;
  height: 2px;
  background: #f0d060;
  border-radius: 2px;
  transition: transform 0.2s, opacity 0.2s;

  ${({ $open, $index }) =>
    $open &&
    $index === 0 &&
    css`transform: translateY(7px) rotate(45deg);`}
  ${({ $open, $index }) =>
    $open &&
    $index === 1 &&
    css`opacity: 0; transform: scaleX(0);`}
  ${({ $open, $index }) =>
    $open &&
    $index === 2 &&
    css`transform: translateY(-7px) rotate(-45deg);`}
`;

/* ─── Sidebar overlay + panel ──────────────────────────────────────────────── */

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0);    opacity: 1; }
  to   { transform: translateX(100%); opacity: 0; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
  display: ${({ $open }) => ($open ? 'block' : 'none')};
`;

const SidePanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1150;
  width: 420px;
  max-width: 92vw;
  background: #111118;
  border-left: 1px solid #2a2a3a;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: -4px 0 32px rgba(0, 0, 0, 0.6);

  animation: ${({ $open }) => ($open ? slideIn : slideOut)} 0.22s ease both;
  pointer-events: ${({ $open }) => ($open ? 'all' : 'none')};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #2a2a3a;
  flex-shrink: 0;
`;

const PanelTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #aaa;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PanelBrand = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #61dafb;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #2a2a3a;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
  @media (orientation: landscape) and (max-height: 500px) {
    display: block;
  }
`;

const PanelAuth = styled.div`
  padding: 8px 8px 4px;
  border-bottom: 1px solid #2a2a3a;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
  @media (orientation: landscape) and (max-height: 500px) {
    display: block;
  }
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 4px;
  &:hover { color: #ccc; background: rgba(255,255,255,0.07); }
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  /* custom scrollbar */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #1a1a2a; }
  &::-webkit-scrollbar-thumb { background: #444; border-radius: 3px; }
`;

/* ─── Main content (full width, no top offset) ─────────────────────────────── */

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

/* ─── Component ────────────────────────────────────────────────────────────── */

const DraftsPageContent = () => {
  const location = useLocation();
  const isEmbedView = location.pathname === '/embed';
  const {
    importDraftData,
    scenariosFirstPick,
    scenariosSecondPick,
    mapDrafts,
    selectedMap
  } = useDraft();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const panelRef = useRef(null);

  // Check for pending draft load on mount (after login OR on embed entry)
  useEffect(() => {
    const pendingDraft = sessionStorage.getItem('pendingDraftLoad');
    if (pendingDraft) {
      try {
        const draftData = JSON.parse(pendingDraft);
        importDraftData(draftData, /* skipConfirmation */ true);
        sessionStorage.removeItem('pendingDraftLoad');
      } catch (error) {
        console.error('❌ Error loading pending draft:', error);
        sessionStorage.removeItem('pendingDraftLoad');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Embed view: persist current draft to sessionStorage on every change so
  // closing/reopening the overlay (parent host writes pendingDraftLoad back
  // from this snapshot) preserves user edits within the session.
  useEffect(() => {
    if (!isEmbedView) return;
    try {
      const snapshot = {
        id: `embed_snapshot_${Date.now()}`,
        layoutVersion: '4.0',
        scenariosFirstPick,
        scenariosSecondPick,
        selectedMap,
        mapDrafts
      };
      sessionStorage.setItem('embedDraftSnapshot', JSON.stringify(snapshot));
    } catch (e) {
      /* ignore quota errors */
    }
  }, [isEmbedView, scenariosFirstPick, scenariosSecondPick, selectedMap, mapDrafts]);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <>
      {!isEmbedView && (
        <>
          {/* Floating hamburger */}
          <HamburgerBtn onClick={() => setSidebarOpen(o => !o)} title="Drafts library">
            <Bar $open={sidebarOpen} $index={0} />
            <Bar $open={sidebarOpen} $index={1} />
            <Bar $open={sidebarOpen} $index={2} />
          </HamburgerBtn>

          {/* Backdrop */}
          <Backdrop $open={sidebarOpen} onClick={closeSidebar} />

          {/* Slide-in panel */}
          <SidePanel $open={sidebarOpen} ref={panelRef}>
            <PanelBrand>System Comes First</PanelBrand>
            <PanelAuth><Auth /></PanelAuth>
            <PanelHeader>
              <PanelTitle>Draft Library</PanelTitle>
              <CloseBtn onClick={closeSidebar}>✕</CloseBtn>
            </PanelHeader>
            <PanelBody>
              <SavedDrafts onDraftLoaded={closeSidebar} />
            </PanelBody>
          </SidePanel>
        </>
      )}

      {/* DraftBoard — full width, no top box */}
      <ContentContainer>
        <DraftBoard isEmbedView={isEmbedView} />
      </ContentContainer>
    </>
  );
};

export default DraftsPageContent;
